import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "./i18n";
import { useTranslation } from "react-i18next";
import { LanguageRedirect, LanguageWrapper, SUPPORTED_LANGUAGES } from "@/utils/languageRouter";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AnalyticsTracker from "@/components/AnalyticsTracker";

// Public Pages
import HomePage from "@/pages/HomePage";
import CircuitsPage from "@/pages/CircuitsPage";
import CircuitDetailPage from "@/pages/CircuitDetailPage";
import TeamPage from "@/pages/TeamPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import ContactPage from "@/pages/ContactPage";
import MapPage from "@/pages/MapPage";
import CheckoutPage from "@/pages/CheckoutPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import GalleryPage from "@/pages/GalleryPage";
import StagesPage from "@/pages/StagesPage";
import StageDetailPage from "@/pages/StageDetailPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import MyReservationsPage from "@/pages/MyReservationsPage";
import DeparturesPage from "@/pages/DeparturesPage";
import DepartureCityPage from "@/pages/DepartureCityPage";
import ExperiencesPage from "@/pages/ExperiencesPage";
import CGVPage from "@/pages/CGVPage";

// Admin Pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCircuits from "@/pages/admin/AdminCircuits";
import AdminReservations from "@/pages/admin/AdminReservations";
import AdminTripDates from "@/pages/admin/AdminTripDates";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminStages from "@/pages/admin/AdminStages";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminCollaborators from "@/pages/admin/AdminCollaborators";
import AdminHeroSlides from "@/pages/admin/AdminHeroSlides";
import AdminSeedData from "@/pages/admin/AdminSeedData";
import AdminExperiences from "@/pages/admin/AdminExperiences";

// Collaborator Pages
import CollaboratorLogin from "@/pages/collaborator/CollaboratorLogin";
import CollaboratorDashboard from "@/pages/collaborator/CollaboratorDashboard";

// Public routes component
const PublicRoutes = () => {
  return (
    <Routes>
      <Route index element={<HomePage />} />
      <Route path="circuits" element={<CircuitsPage />} />
      <Route path="circuits/4x4" element={<CircuitsPage type="4x4" />} />
      <Route path="circuits/trekking" element={<CircuitsPage type="trekking" />} />
      <Route path="circuit/:id" element={<CircuitDetailPage />} />
      <Route path="departures" element={<DeparturesPage />} />
      <Route path="depart/:city" element={<DepartureCityPage />} />
      <Route path="stages" element={<StagesPage />} />
      <Route path="stages/:slug" element={<StageDetailPage />} />
      <Route path="gallery" element={<GalleryPage />} />
      <Route path="blog" element={<BlogPage />} />
      <Route path="blog/:slug" element={<BlogPostPage />} />
      <Route path="map" element={<MapPage />} />
      <Route path="team" element={<TeamPage />} />
      <Route path="testimonials" element={<TestimonialsPage />} />
      <Route path="experiences" element={<ExperiencesPage />} />
      <Route path="contact" element={<ContactPage />} />
      <Route path="cgv" element={<CGVPage />} />
      <Route path="checkout/:reservationId" element={<CheckoutPage />} />
      <Route path="checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="checkout/cancel" element={<CircuitsPage />} />
      <Route path="my-reservations" element={<MyReservationsPage />} />
      <Route path="reservation/success" element={<CheckoutSuccessPage />} />
      <Route path="reservation/cancel" element={<CircuitsPage />} />
    </Routes>
  );
};

function App() {
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState("ltr");

  useEffect(() => {
    const dir = i18n.language === "ar" ? "rtl" : "ltr";
    setDirection(dir);
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <div className="App min-h-screen flex flex-col" dir={direction}>
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          {/* Root redirect - detects browser language */}
          <Route path="/" element={<LanguageRedirect />} />
          
          {/* Admin Routes - no language prefix */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          {/* Redirect /{lang}/admin/* to /admin/* */}
          <Route path="/:lang/admin/*" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="circuits" element={<AdminCircuits />} />
            <Route path="reservations" element={<AdminReservations />} />
            <Route path="trip-dates" element={<AdminTripDates />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="gallery" element={<AdminGallery />} />
            <Route path="stages" element={<AdminStages />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="collaborators" element={<AdminCollaborators />} />
            <Route path="hero-slides" element={<AdminHeroSlides />} />
            <Route path="seed-data" element={<AdminSeedData />} />
            <Route path="experiences" element={<AdminExperiences />} />
          </Route>

          {/* Collaborator Routes - no language prefix */}
          <Route path="/collaborator/login" element={<CollaboratorLogin />} />
          <Route path="/collaborator/dashboard" element={<CollaboratorDashboard />} />

          {/* Language-prefixed public routes */}
          <Route
            path="/:lang/*"
            element={
              <LanguageWrapper>
                <Navbar />
                <main className="flex-1">
                  <PublicRoutes />
                </main>
                <Footer />
              </LanguageWrapper>
            }
          />
          
          {/* Catch-all redirect to default language */}
          <Route path="*" element={<Navigate to="/fr/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
